'use client'

import posthog from 'posthog-js'

import { analyticsConsentGranted } from '@/lib/analytics/consent'

/**
 * Typed PostHog event layer for the MyCIO growth plan (instrumentation-brief.md P1).
 *
 * The whole funnel / per-loop k-factor / retention analysis depends on these events being emitted with
 * exactly these names + property shapes — PostHog funnels and cohort insights are built by matching on
 * them, so DO NOT rename an event or drop a property without updating the PostHog insights too.
 *
 * Everything here is a safe no-op until posthog.init() has run (SSR, missing key, ad-blocked-but-unproxied
 * fallback) — `posthog.__loaded` gates it — so callers can fire events unconditionally.
 */
export type ShareLoop = 'plan_share' | 'referral' | 'value_map' | 'household' | 'template'
export type ShareUnit = 'confidence_band' | 'plan_summary' | 'value_map' | 'invite'
export type ShareChannel = 'web_share' | 'email' | 'copy_link' | 'whatsapp' | 'x' | 'linkedin'
export type PaidTier = 'living' | 'plus' | 'wealth' | 'cross_border' | 'advisor'

type EventMap = {
  page_view: { source?: string }
  instant_run: { goal?: string; jurisdiction?: string }
  claim: Record<string, never>
  share_prompt_shown: { loop: ShareLoop; unit: ShareUnit }
  share_initiated: { loop: ShareLoop; unit: ShareUnit; channel: ShareChannel }
  share_sent: { channel: ShareChannel }
  invite_clicked: { loop: ShareLoop }
  invitee_ran_instant: Record<string, never>
  invitee_claimed: Record<string, never>
  paid: { tier: PaidTier }
}

function ready(): boolean {
  // posthog.__loaded is set true by init(); guards SSR and un-initialized/no-key environments.
  return typeof window !== 'undefined' && Boolean((posthog as unknown as { __loaded?: boolean }).__loaded)
}

/**
 * Whether analytics is CONFIGURED for this build (the PostHog key was baked in) — distinct from ready():
 * configured-but-still-loading means "wait and retry", unconfigured means "no event can EVER capture".
 * Callers that hold user-visible flows for an event (the checkout paid{tier} poll) must skip the hold
 * entirely when this is false — otherwise an inert analytics config parks users for the full poll window
 * on an event that can never emit.
 */
export function analyticsConfigured(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY)
}

/**
 * Emit a typed growth event. Props are required exactly as the schema declares (empty object for none).
 * Returns whether it actually captured — callers of terminal events (paid) use this to avoid marking a
 * conversion "sent" when PostHog hasn't loaded yet (it would have silently dropped).
 */
export function track<E extends keyof EventMap>(event: E, props: EventMap[E]): boolean {
  if (!ready()) return false
  posthog.capture(event, props as Record<string, unknown>)
  return true
}

/**
 * Stitch the anonymous pre-signup session (the /instant Value Map run) to the account. CRITICAL for the
 * funnel: without identify(), "anonymous instant → claim → paid" can't be measured as one journey.
 * posthog.identify aliases the current anonymous distinct_id to userId automatically. Person props are
 * set with $set (latest wins) — pass what you know; omit the rest.
 */
export function identifyMyCIOUser(
  userId: string,
  props?: { avatar?: string; tier?: string; jurisdiction?: string; email?: string },
): void {
  // Identity (and the email inside it) is PII: it crosses to PostHog only AFTER the banner's yes.
  // posthog drops capture() while opted out, but identify/$set is not a capture — gate it here.
  if (!ready() || !userId || !analyticsConsentGranted()) return
  const set: Record<string, unknown> = {}
  if (props?.avatar) set.avatar = props.avatar
  if (props?.tier) set.tier = props.tier
  if (props?.jurisdiction) set.jurisdiction = props.jurisdiction
  if (props?.email) set.email = props.email
  posthog.identify(userId, Object.keys(set).length ? set : undefined)
}

/** Clear identity on sign-out so the next (possibly different) user starts a fresh anonymous id. */
export function resetAnalytics(): void {
  if (!ready()) return
  posthog.reset()
}
